import { useCallback } from 'react';
import { useReturnStore } from '@src/stores/returnStore';
import { ChosenBoxCounts } from '@src/utils/upsGroupingUtil';
import { isROSEnabled } from '@src/constants/fs-values';
import { useReturnMethods } from '@src/hooks/useReturnMethods';
import useUpsGrouping from '@src/hooks/useUpsGrouping';

/**
 * What a refresh means for the screen that asked.
 *
 * `blocked` is neither success nor breakage, and the difference is the whole
 * reason there are three: the customer's change cannot stand, but nothing went
 * wrong that they should be told about. It covers a fraud decision — they were
 * answered, not failed — and an attempt that no longer owns the screen. Showing
 * the technical error for either would say something untrue.
 */
export type ReturnOptionsRefreshOutcome = 'ok' | 'failed' | 'blocked';

/**
 * Re-asks the two services that describe a return, for the screens that let the
 * customer change one after it has already been described.
 *
 * They are two calls rather than one, with different scopes, and both facts
 * matter:
 *
 * - **Return Methods** re-issues the encrypted token and refreshes per-item
 *   eligibility. It carries the whole remaining selection across every method,
 *   because the token is order-scoped — a call narrowed to the edited leg would
 *   mint a token that rejects the other legs when the return is created.
 * - **The grouping dry run** re-groups the parcel items and returns their box
 *   floors, and carries only those items. It mints nothing.
 *
 * Which of the two tokens the return is actually submitted under is neither
 * call's to decide, and today it is not this one's: `getCurrentReturnET` still
 * prefers the validator's, because the validator also re-issues and runs last
 * of the three. The Return Methods token becomes the submitted one when the
 * validator stops issuing one and the create call checks the methods token
 * instead — see the ordering note in `returnToken.ts`. What this call
 * guarantees meanwhile is that both are re-issued for the same edit, so which
 * one wins cannot describe a selection the customer has moved on from.
 *
 * They are exposed separately rather than as one sequence because something has
 * to happen between them: the legs are rebuilt for the new selection in
 * between, and the grouping is applied on top of that rebuild.
 *
 * Off the migration switch both are no-ops that report `ok`. Nothing re-asked
 * these services before the switch existed, and the path that runs instead
 * still refreshes everything it did — so reporting anything else here would
 * hold a customer for a call that was never going to be made.
 */
export const useReturnOptionsRefresh = () => {
  const {
    fetchReturnMethods,
    getLastAttemptOutcome,
    fraudOutcome,
    clearFraudOutcome,
  } = useReturnMethods();
  const { fetchUpsGrouping } = useUpsGrouping();

  const refreshReturnMethods =
    useCallback(async (): Promise<ReturnOptionsRefreshOutcome> => {
      if (!isROSEnabled()) return 'ok';
      const adaptedMethods = await fetchReturnMethods();
      if (adaptedMethods) return 'ok';
      // Undefined covers three unrelated outcomes, so which one it was is asked
      // rather than assumed — a declined customer must not be told the service
      // broke.
      return getLastAttemptOutcome() === 'failed' ? 'failed' : 'blocked';
    }, [fetchReturnMethods, getLastAttemptOutcome]);

  const refreshGrouping = useCallback(
    async (
      // Handed in by whoever owns the submission rather than held here. The
      // counts have to be read before the legs are rebuilt and spent after, so
      // they outlive neither call on their own — and kept on the hook, one
      // submission could spend another's, which on the review screen would
      // silently reset a count the customer had raised.
      chosenCounts: ChosenBoxCounts,
    ): Promise<ReturnOptionsRefreshOutcome> => {
      if (!isROSEnabled()) return 'ok';
      // The selection is read here rather than taken from the caller, and the
      // difference is not cosmetic: folding in the methods answer can withdraw
      // a method an item no longer qualifies for, so the list the caller was
      // holding a moment ago may still name a method this call would group by.
      // The store holds the one the answer has been applied to.
      const grouped = await fetchUpsGrouping(
        useReturnStore.getState().selectedItems,
        chosenCounts,
      );
      if (grouped === 'ok') return 'ok';
      return grouped === 'failed' ? 'failed' : 'blocked';
    },
    [fetchUpsGrouping],
  );

  return {
    refreshReturnMethods,
    refreshGrouping,
    // Handed straight back rather than acted on here. Which prompt a declined
    // or warned customer sees on this screen, and what it does to their change,
    // is a decision of its own and is not made here.
    fraudOutcome,
    clearFraudOutcome,
  };
};

export default useReturnOptionsRefresh;
